<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
      <header class="mb-8">
        <div class="flex items-center justify-center sm:justify-start">
          <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 text-center sm:text-left">
            Dinheiro no
            <span class="text-brand-600 dark:text-brand-500">Cofre</span>
          </h1>
          <button @click="toggleVisibility"
            class="ml-3 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-2 rounded-full transition-colors"
            :title="showValues ? 'Ocultar valores' : 'Mostrar valores'">
            <span v-if="showValues" class="material-symbols-outlined text-xl sm:text-2xl">visibility</span>
            <span v-else class="material-symbols-outlined text-xl sm:text-2xl">visibility_off</span>
          </button>
        </div>
        <p class="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left mt-1">
          Gerencie o dinheiro guardado fisicamente.
        </p>
      </header>

      <div class="mb-8 flex justify-start">
        <button @click="showAddDinheiroCofreModal" class="btn-primary flex items-center justify-center text-sm">
          <span class="material-symbols-outlined mr-2 text-lg">add_circle</span>
          Adicionar Dinheiro
        </button>
      </div>

      <div v-if="sortedDinheiroCofreList.length > 0" class="mb-8">
        <div
          class="bg-gradient-to-r from-brand-600 to-brand-700 text-white p-6 rounded-xl shadow-lg flex flex-col sm:flex-row justify-between items-center">
          <div class="mb-2 sm:mb-0 text-center sm:text-left">
            <span class="text-base sm:text-lg font-medium">Valor Total no Cofre</span>
          </div>
          <div>
            <span class="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{{ displayCurrency(totalDinheiroCofre) }}</span>
          </div>
        </div>
      </div>

      <div v-if="sortedDinheiroCofreList.length === 0" class="empty-state">
        <span class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">savings</span>
        <p class="mt-4 font-medium">Seu cofre está vazio.</p>
        <p class="text-sm">Adicione um valor para começar a economizar.</p>
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="item in sortedDinheiroCofreList" :key="item.id" class="card-cofre">
          <div class="p-5 bg-gradient-to-tr from-brand-700 to-brand-800 text-white flex items-center rounded-t-2xl">
            <span class="material-symbols-outlined text-2xl sm:text-3xl mr-4 p-2 bg-white/10 rounded-full">savings</span>
            <div>
              <h3 class="font-bold text-xl sm:text-2xl tracking-wider">{{ displayCurrency(item.valor) }}</h3>
            </div>
          </div>
          <div class="p-4 flex-grow">
            <p class="text-sm text-slate-500 dark:text-slate-400">Valor registrado no cofre.</p>
          </div>
          <div class="card-footer">
            <button @click="editDinheiroCofre(item)" title="Editar Valor"
              class="btn-action-icon text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-slate-600">
              <span class="material-symbols-outlined text-lg">edit</span>
            </button>
            <button @click="confirmDelete(item.id)" title="Excluir Registro"
              class="btn-action-icon text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-slate-600">
              <span class="material-symbols-outlined text-lg">delete</span>
            </button>
          </div>
        </div>
      </div>

      <transition name="modal-fade">
        <div v-if="showModal" @click.self="closeModal" class="modal-backdrop">
          <div class="modal-container">
            <header class="modal-header">
              <h3 class="text-xl font-semibold">
                {{ isEditing ? 'Editar Dinheiro no Cofre' : 'Adicionar Dinheiro ao Cofre' }}
              </h3>
              <button @click="closeModal" title="Fechar" :disabled="isSubmitting" class="modal-close-btn">
                <span class="material-symbols-outlined">close</span>
              </button>
            </header>

            <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
              <div>
                <label for="cofreValor" class="form-label">Valor (R$)</label>
                <input id="cofreValor" v-model.number="dinheiroCofre.valor" type="number" step="0.01" required
                  placeholder="0.00" class="form-input" />
              </div>
              <footer class="modal-footer !p-0 !pt-4 !bg-transparent">
                <button @click="closeModal" type="button" :disabled="isSubmitting" class="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center">
                  <span v-if="isSubmitting"
                    class="material-symbols-outlined animate-spin mr-2 text-lg">progress_activity</span>
                  {{ isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Adicionar') }}
                </button>
              </footer>
            </form>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false" class="modal-backdrop">
          <div class="modal-container p-0 max-w-md">
            <div class="p-6 text-center">
              <span class="material-symbols-outlined text-5xl text-red-500 dark:text-red-400 mx-auto">warning</span>
              <h3 class="modal-title">Excluir Registro</h3>
              <p class="modal-text">Tem certeza que deseja excluir este registro do cofre?</p>
            </div>
            <div class="modal-footer">
              <button @click="showDeleteConfirm = false" type="button" class="btn-secondary">Cancelar</button>
              <button @click="executeDelete" class="btn-danger">Sim, Excluir</button>
            </div>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div v-if="showUnsavedChangesConfirm" @click.self="showUnsavedChangesConfirm = false" class="modal-backdrop">
          <div class="modal-container p-0 max-w-md">
            <div class="p-6 text-center">
              <span class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">warning</span>
              <h3 class="modal-title">Descartar Alterações?</h3>
              <p class="modal-text">Você possui alterações não salvas. Deseja sair e perder os dados?</p>
            </div>
            <div class="modal-footer">
              <button @click="showUnsavedChangesConfirm = false" type="button" class="btn-secondary">Cancelar</button>
              <button @click="handleConfirmClose" class="btn-danger">Sim, Descartar</button>
            </div>
          </div>
        </div>
      </transition>

    </div>
  </div>
</template>


<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'

const showValues = ref(true)

const dinheiroCofreList = ref([])
const showModal = ref(false)
const isEditing = ref(false)
const isSubmitting = ref(false)
const showDeleteConfirm = ref(false)
const itemParaDeletar = ref(null)
const dinheiroCofre = ref({ id: null, valor: 0 })
const apiUrl = 'https://backend.controlease.com.br/dinheirocofre'

const pristineDinheiroCofre = ref(null)
const showUnsavedChangesConfirm = ref(false)

const isFormDirty = computed(() => {
  if (!pristineDinheiroCofre.value) return false
  return dinheiroCofre.value.valor !== pristineDinheiroCofre.value.valor
})

const toggleVisibility = () => {
  showValues.value = !showValues.value
  localStorage.setItem('showFinancialValues', JSON.stringify(showValues.value))
}

const displayCurrency = (value) => {
  if (showValues.value) return formatCurrency(value)
  return 'R$ ●●●●●'
}

const formatCurrency = (value) => {
  if (typeof value !== 'number') return 'R$ 0,00'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const authConfig = () => {
  const token = localStorage.getItem('token')
  if (token) return { headers: { Authorization: `Bearer ${token}` } }
  return {}
}

const fetchDinheiroCofre = async () => {
  try {
    const response = await axios.get(apiUrl, authConfig())
    dinheiroCofreList.value = response.data
  } catch (error) {
    console.error('Erro ao buscar dinheiro no cofre:', error)
  }
}

const showAddDinheiroCofreModal = () => {
  resetForm()
  pristineDinheiroCofre.value = { ...dinheiroCofre.value }
  showModal.value = true
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  try {
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${dinheiroCofre.value.id}`, dinheiroCofre.value, authConfig())
    } else {
      await axios.post(apiUrl, dinheiroCofre.value, authConfig())
    }
    await fetchDinheiroCofre()
    forceCloseModal()
  } catch (error) {
    console.error('Erro ao salvar dinheiro no cofre:', error)
    alert('Erro ao salvar dinheiro no cofre.')
  } finally {
    isSubmitting.value = false
  }
}

const editDinheiroCofre = (itemToEdit) => {
  dinheiroCofre.value = { ...itemToEdit }
  pristineDinheiroCofre.value = { ...itemToEdit }
  isEditing.value = true
  showModal.value = true
}

const confirmDelete = (id) => {
  itemParaDeletar.value = id
  showDeleteConfirm.value = true
}

const executeDelete = async () => {
  if (itemParaDeletar.value === null) return
  try {
    await axios.delete(`${apiUrl}/${itemParaDeletar.value}`, authConfig())
    await fetchDinheiroCofre()
  } catch (error) {
    console.error('Erro ao excluir dinheiro no cofre:', error)
    alert('Erro ao excluir dinheiro no cofre.')
  } finally {
    showDeleteConfirm.value = false
    itemParaDeletar.value = null
  }
}

const closeModal = () => {
  if (isSubmitting.value) return
  if (isFormDirty.value) {
    showUnsavedChangesConfirm.value = true
  } else {
    forceCloseModal()
  }
}

const forceCloseModal = () => {
  showModal.value = false
  resetForm()
}

const handleConfirmClose = () => {
  showUnsavedChangesConfirm.value = false
  forceCloseModal()
}

const resetForm = () => {
  dinheiroCofre.value = { id: null, valor: 0 }
  isEditing.value = false
  pristineDinheiroCofre.value = null
}

const sortedDinheiroCofreList = computed(() => {
  if (!dinheiroCofreList.value) return []
  return [...dinheiroCofreList.value].sort((a, b) => b.id - a.id)
})

const totalDinheiroCofre = computed(() =>
  (dinheiroCofreList.value || []).reduce((total, item) => total + (parseFloat(item.valor) || 0), 0)
)

onMounted(() => {
  const savedVisibility = localStorage.getItem('showFinancialValues')
  if (savedVisibility !== null) showValues.value = JSON.parse(savedVisibility)
  fetchDinheiroCofre()
})
</script>

<style scoped>
.form-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1;
}
.form-input {
  @apply w-full px-3 py-2 text-sm bg-white dark:bg-slate-700/60 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-70 focus:border-brand-500 text-slate-800 dark:text-slate-200;
}
.btn-primary {
  @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed;
}
.btn-secondary {
  @apply px-5 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed;
}
.btn-danger {
  @apply px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-150 ease-in-out;
}
.btn-action-icon {
  @apply p-1.5 rounded-full transition-colors duration-200;
}
.card-cofre {
  @apply bg-white dark:bg-brand-800 shadow-lg rounded-2xl flex flex-col overflow-hidden transition-transform hover:-translate-y-1 duration-300;
}
.card-footer {
  @apply mt-auto p-2 bg-slate-50 dark:bg-brand-800/50 flex items-center justify-end space-x-2;
}
.empty-state {
  @apply text-center py-16 text-slate-500 dark:text-slate-400 bg-white dark:bg-brand-800 rounded-xl shadow-md flex flex-col justify-center items-center;
}
.modal-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 dark:bg-brand-900/80 backdrop-blur-sm p-4;
}
.modal-container {
  @apply bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md transform transition-all;
}
.modal-header {
  @apply px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center;
}
.modal-close-btn {
  @apply p-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
}
.modal-footer {
  @apply px-4 py-3 bg-slate-50 dark:bg-slate-700/50 flex justify-end space-x-3 rounded-b-xl;
}
.modal-title {
  @apply mt-2 text-xl font-semibold text-slate-800 dark:text-slate-100;
}
.modal-text {
  @apply mt-2 text-sm text-slate-600 dark:text-slate-400;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
